import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import Logo from '../components/Logo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TAGS = [
  'cover_drive', 'straight_drive', 'pull', 'sweep', 'defence_front',
  'yorker', 'inswing', 'outswing', 'leg_spin', 'off_spin',
  'bouncer', 'diving_catch', 'direct_hit',
];

/**
 * Curator workflow.
 *
 *   - Pending queue: review YouTube/Twitter ingestion suggestions.
 *   - Approve → publishes + generates the AI breakdown.
 *   - Reject → marks rejected with a reason.
 *   - Ingest panel: trigger fresh YouTube/Twitter searches.
 *   - Re-seed button: re-runs the curated starter set.
 *
 * Gated by ADMIN_ACCOUNT_IDS on the backend; non-admins get a 403
 * banner here too.
 */
export default function AdminClips() {
  const { token } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [query, setQuery] = useState('cover drive cricket');
  const [tags, setTags] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/clips/admin/pending`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPending(r.data?.clips || []);
      setForbidden(false);
    } catch (err) {
      if (err.response?.status === 403) setForbidden(true);
      else toast.error('Could not load pending clips');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const approve = async (id) => {
    try {
      await axios.post(`${API}/clips/admin/${id}/approve`, { publish: true }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Approved + breakdown generated');
      setPending((p) => p.filter((c) => c.id !== id));
    } catch { toast.error('Approve failed'); }
  };

  const reject = async (id) => {
    const reason = window.prompt('Why reject this clip?', 'wrong technique');
    if (!reason) return;
    try {
      await axios.post(`${API}/clips/admin/${id}/reject`, { reason }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPending((p) => p.filter((c) => c.id !== id));
    } catch { toast.error('Reject failed'); }
  };

  const ingest = async (provider) => {
    try {
      const r = await axios.post(`${API}/clips/admin/ingest/${provider}`, {
        query, technique_tags: tags, max_results: 25,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`${provider}: ${r.data.suggested} new suggestions`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ingest failed');
    }
  };

  const reseed = async () => {
    try {
      const r = await axios.post(`${API}/clips/admin/seed`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Seeded ${r.data.inserted} clips`);
      load();
    } catch { toast.error('Seed failed'); }
  };

  const toggleTag = (t) =>
    setTags((arr) => arr.includes(t) ? arr.filter((x) => x !== t) : [...arr, t]);

  return (
    <div
      className="min-h-screen"
      style={{
        background: theme === 'dark'
          ? 'linear-gradient(180deg, #060818, #0a0e27)'
          : 'linear-gradient(180deg, #f8fafc, #eef2f7)',
        color: theme === 'dark' ? '#f7fafc' : '#0f172a',
      }}
    >
      <header className="max-w-5xl mx-auto px-4 pt-6 flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="opacity-70 hover:opacity-100">← Back</button>
        <Logo size={28} />
        <div className="flex-1" />
        <button onClick={toggleTheme} className="p-2 rounded-lg" style={{ background: 'rgba(255,255,255,0.06)' }}>
          {theme === 'dark' ? '☀' : '☾'}
        </button>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6 space-y-5">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight">Curator workflow</h1>
          <p className="text-sm opacity-70 mt-1">Approve, reject, or ingest fresh clips.</p>
        </div>

        {forbidden ? (
          <Card theme={theme}>
            <div className="text-sm opacity-80">
              You're not an admin. Add your account ID to the
              <code className="mx-1 px-1 rounded" style={{ background: 'rgba(255,255,255,0.08)' }}>
                ADMIN_ACCOUNT_IDS
              </code>
              env var on the backend, then restart.
            </div>
          </Card>
        ) : (
          <>
            <Card theme={theme} title="Ingest fresh suggestions">
              <div className="space-y-3">
                <input
                  value={query} onChange={(e) => setQuery(e.target.value)}
                  placeholder="search query (e.g. cover drive cricket)"
                  className="w-full px-3 py-2 rounded-lg"
                  style={{
                    background: 'rgba(255,255,255,0.06)',
                    border: '1px solid rgba(150,160,180,0.25)', color: 'inherit',
                  }}
                />
                <div className="flex flex-wrap gap-1.5">
                  {TAGS.map((t) => (
                    <button
                      key={t}
                      onClick={() => toggleTag(t)}
                      className="text-xs px-2.5 py-1 rounded-full"
                      style={{
                        background: tags.includes(t) ? 'rgba(0,201,167,0.18)' : 'rgba(255,255,255,0.04)',
                        border: `1px solid ${tags.includes(t) ? 'rgba(0,201,167,0.4)' : 'rgba(150,160,180,0.2)'}`,
                        color: tags.includes(t) ? '#00C9A7' : 'inherit',
                      }}
                    >
                      {t.replace(/_/g, ' ')}
                    </button>
                  ))}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => ingest('youtube')}
                    className="px-3 py-2 rounded-lg text-sm font-semibold text-white"
                    style={{ background: 'linear-gradient(135deg, #00A651, #00D9FF)' }}
                  >
                    Ingest from YouTube
                  </button>
                  <button
                    onClick={() => ingest('twitter')}
                    className="px-3 py-2 rounded-lg text-sm font-semibold text-white"
                    style={{ background: 'rgba(255,255,255,0.08)' }}
                  >
                    Ingest from Twitter / X
                  </button>
                  <button
                    onClick={reseed}
                    className="px-3 py-2 rounded-lg text-sm opacity-70 hover:opacity-100"
                  >
                    Re-run starter seed
                  </button>
                </div>
              </div>
            </Card>

            <Card theme={theme} title={`Pending (${pending.length})`}>
              {loading ? (
                <p className="opacity-60 text-sm">Loading…</p>
              ) : pending.length === 0 ? (
                <p className="opacity-60 text-sm">Queue is empty.</p>
              ) : (
                <ul className="divide-y" style={{ borderColor: 'rgba(150,160,180,0.15)' }}>
                  {pending.map((c) => (
                    <PendingRow key={c.id} clip={c} onApprove={approve} onReject={reject} />
                  ))}
                </ul>
              )}
            </Card>
          </>
        )}
      </main>
    </div>
  );
}

function Card({ title, theme, children }) {
  return (
    <section
      className="rounded-2xl p-5"
      style={{
        background: theme === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.85)',
        border: '1px solid rgba(0,201,167,0.15)',
      }}
    >
      {title && (
        <h2 className="text-[11px] uppercase tracking-widest opacity-60 font-bold mb-3">{title}</h2>
      )}
      {children}
    </section>
  );
}

function PendingRow({ clip, onApprove, onReject }) {
  return (
    <li className="py-3 flex items-start gap-3">
      <div style={{ width: 96, height: 56, background: '#000', borderRadius: 6, overflow: 'hidden', flexShrink: 0 }}>
        {clip.thumbnail_url && (
          <img src={clip.thumbnail_url} alt="" className="w-full h-full object-cover" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-sm leading-tight">{clip.title}</div>
        <div className="text-xs opacity-60 mt-0.5">
          {clip.source} · {clip.duration_seconds || '?'}s
          {clip.is_official_channel && (
            <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] font-bold" style={{ background: 'rgba(0,201,167,0.18)', color: '#00C9A7' }}>
              OFFICIAL
            </span>
          )}
        </div>
        <div className="text-xs opacity-60 mt-1 truncate">{clip.source_url}</div>
        <div className="flex flex-wrap gap-1 mt-1.5">
          {(clip.technique_tags || []).map((t) => (
            <span key={t} className="text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded"
                  style={{ background: 'rgba(0,201,167,0.12)', color: '#00C9A7' }}>
              {t.replace(/_/g, ' ')}
            </span>
          ))}
        </div>
      </div>
      <div className="flex flex-col gap-1 shrink-0">
        <button
          onClick={() => onApprove(clip.id)}
          className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white"
          style={{ background: 'linear-gradient(135deg, #00A651, #00D9FF)' }}
        >
          Approve
        </button>
        <button
          onClick={() => onReject(clip.id)}
          className="px-3 py-1.5 rounded-lg text-xs"
          style={{ background: 'rgba(239,68,68,0.12)', color: '#ef4444' }}
        >
          Reject
        </button>
      </div>
    </li>
  );
}
